import React, { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Outlet, useNavigate } from 'react-router';
import { useStore } from '@link/agent/stores';
import agentAuth from '@link/agent/vendors/firebase';
import { AutoLogoutDialog, Loader } from '@link/share/components';
import { clearAutoLogoutStorage, useAutoLogout } from '@link/share/hooks';
import Header from '../Header';
import Sidebar from '../Sidebar';
import styles from './base.module.css';

const BaseLayout: React.FC = () => {
  const { auth, agent } = useStore();
  const navigate = useNavigate();

  const handleAutoLogout = useCallback(async () => {
    clearAutoLogoutStorage();
    await agentAuth.logout();
    agent.clearSelectedAgentInfo();
  }, [agent]);

  const { warningVisible, remainingSeconds, onContinue } =
    useAutoLogout(handleAutoLogout);

  useEffect(() => {
    if (auth.isLinkAdmin && !agent.allOtasLoaded) {
      agent.getAllOtas();
    }
  }, [auth.isLinkAdmin, agent.allOtasLoaded, agent]);

  useEffect(() => {
    if (auth.isLinkAdmin && agent.allOtasLoaded) {
      agent.initFromSessionStorage();
    }
  }, [auth.isLinkAdmin, agent.allOtasLoaded, agent]);

  useEffect(() => {
    if (!auth.loading && auth.isUnauthorizedUser) {
      auth.clearUser();
      navigate('/login', { replace: true });
    }
  }, [auth.isUnauthorizedUser, auth.loading, auth, navigate]);

  useEffect(() => {
    if (!auth.isLinkAdmin && auth.userAgentId) {
      agent.setSelectedAgentId(auth.userAgentId);
      agent.setSelectedAgentOrgId(auth.userOtaOrgId);
    }
  }, [auth.isLinkAdmin, auth.userAgentId, auth.userOtaOrgId, agent]);

  return (
    <div className={styles.base}>
      <Header />
      <div className={styles.container}>
        <Sidebar />
        <main className={styles.main}>
          <div className={styles.inner}>
            {agent.selectedAgentOrgId || auth.userOtaOrgId ? (
              <Outlet key={agent.selectedAgentId} />
            ) : (
              <Loader />
            )}
          </div>
        </main>
      </div>
      <AutoLogoutDialog
        visible={warningVisible}
        remainingSeconds={remainingSeconds}
        onContinue={onContinue}
        onLogout={handleAutoLogout}
      />
    </div>
  );
};

const BaseLayoutView = observer(BaseLayout);

export default BaseLayoutView;
